import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import DatePicker from './DatePicker';
import './SalesActivity.css';
import { formatNumberWithCommas, formatCurrencyDisplay, getNumberValue, getCurrencyValue, getLocalDateString, toLocalDateString } from '../utils/formatters';
import LoadingState from './LoadingState';
import ConfirmModal from './ConfirmModal';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

function ServiceInput({ user, managedOwner }) {
  const location = useLocation();
  const [allEntries, setAllEntries] = useState([]);
  const [dealerships, setDealerships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [alertModal, setAlertModal] = useState({ isOpen: false, title: '', message: '' });
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  
  // Filter states
  const [selectedDealerships, setSelectedDealerships] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [viewMode, setViewMode] = useState('service'); // 'service' or 'labor'

  // Bulk import states
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkStep, setBulkStep] = useState('upload');
  const [bulkImportError, setBulkImportError] = useState(null);
  const [bulkImportSuccess, setBulkImportSuccess] = useState(null);
  const [bulkValidation, setBulkValidation] = useState(null);
  const [bulkEntries, setBulkEntries] = useState([]);
  const [bulkFileName, setBulkFileName] = useState('');
  
  const isOwner = user?.role === 'OWNER' || managedOwner;
  const canBulkImport = user?.role === 'OWNER' || user?.role === 'MANAGER' || user?.role === 'ADMIN' || managedOwner;

  useEffect(() => {
    fetchAllData();
  }, [managedOwner]);

  // Open new form when navigated with ?new=true (like DailyLogger)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('new') === 'true') {
      const dealerIdParam = params.get('dealershipId');
      if (dealerships.length > 0) {
        // Use dealerId from URL if provided, else default first
        const defaultDealership = dealerIdParam || dealerships[0].id;
        setEditingEntry({
          dealershipId: defaultDealership,
          date: getLocalDateString(),
          repairOrders: 0,
          hours: 0,
          netSales: 0,
          partsOnROs: 0,
          grossProfit: 0,
          actualHours: 0,
          billableHours: 0,
          availableHours: 0,
          numberOfTechnicians: 0,
          notes: ''
        });
        setShowForm(true);
      } else {
        setEditingEntry({
          dealershipId: dealerIdParam || '',
          date: getLocalDateString(),
          repairOrders: 0,
          hours: 0,
          netSales: 0,
          partsOnROs: 0,
          grossProfit: 0,
          notes: ''
        });
        setShowForm(true);
      }
    }
  }, [location.search, dealerships]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      let dealershipsEndpoint = isOwner 
        ? `${API_URL}/api/owners/dealerships`
        : `${API_URL}/api/dealerships`;
      
      if (managedOwner && isOwner) {
        dealershipsEndpoint += `?ownerId=${managedOwner.id}`;
      }
        
      const dealershipsRes = await fetch(dealershipsEndpoint, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (dealershipsRes.ok) {
        const dealershipsData = await dealershipsRes.json();
        const fetchedDealerships = dealershipsData.dealerships || [];
        setDealerships(fetchedDealerships);
        
        const entriesPromises = fetchedDealerships.map(async (d) => {
          const res = await fetch(
            `${API_URL}/api/dealerships/${d.id}/service-jobs`,
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
          if (res.ok) {
            const data = await res.json();
            return (data.entries || []).map(e => ({
              ...e,
              dealershipId: d.id,
              dealershipName: d.name
            }));
          }
          return [];
        });
        
        const entriesArrays = await Promise.all(entriesPromises);
        const allEntriesFlat = entriesArrays.flat();
        setAllEntries(allEntriesFlat);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNewEntry = () => {
    const defaultDealership = dealerships.length > 0 ? dealerships[0].id : '';
    setEditingEntry({
      dealershipId: defaultDealership,
      date: getLocalDateString(),
      repairOrders: 0,
      hours: 0,
      netSales: 0,
      partsOnROs: 0,
      grossProfit: 0,
      actualHours: 0,
      billableHours: 0,
      availableHours: 0,
      numberOfTechnicians: 0,
      notes: ''
    });
    setShowForm(true);
  };

  const handleEditEntry = (entry) => {
    setEditingEntry(entry);
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingEntry(null);
  };

  const handleDeleteEntry = async (id) => {
    if (!confirm('Are you sure you want to delete this entry?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/service-jobs/${id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete entry');
      }

      setAllEntries(prev => prev.filter(entry => entry.id !== id));
    } catch (error) {
      console.error('Error deleting entry:', error);
      setAlertModal({ isOpen: true, title: 'Error', message: 'Failed to delete entry. Please try again.' });
    }
  };

  // Bulk import functions
  const downloadCSVTemplate = () => {
    const headers = ['Date', 'Dealership ID', 'Repair Orders', 'Hours', 'Net Sales', 'Parts Sold on Repair Orders', 'Gross Profit', 'Actual Hours', 'Billable Hours', 'Available Hours', 'Number of Technicians', 'Notes'];
    const sample = ['2025-01-15', dealerships[0]?.id || 'DEALERSHIP_ID', '25', '120.5', '15000.00', '6000.00', '8500.00', '110.0', '105.0', '160.0', '4', 'Optional notes'];
    const csvContent = [headers.join(','), sample.join(',')].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'service_import_template.csv';
    a.click();
  };

  const parseCsvFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target.result;
          const lines = text.split('\n').filter(line => line.trim());
          if (lines.length < 2) {
            reject(new Error('CSV file must contain headers and at least one data row'));
            return;
          }

          const headers = lines[0].split(',').map(h => h.trim());
          const requiredHeaders = ['Date', 'Dealership ID'];
          const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
          if (missingHeaders.length > 0) {
            reject(new Error(`Missing required columns: ${missingHeaders.join(', ')}`));
            return;
          }

          const entries = [];
          for (let i = 1; i < lines.length; i++) {
            const values = lines[i].split(',').map(v => v.trim());
            const entry = {};
            headers.forEach((header, index) => {
              entry[header.toLowerCase().replace(/ /g, '')] = values[index] || '';
            });
            entries.push({
              date: entry.date,
              dealershipId: entry.dealershipid,
              repairOrders: entry.repairorders,
              hours: entry.hours,
              netSales: entry.netsales,
              partsOnROs: entry.partssoldonrepairorders,
              grossProfit: entry.grossprofit,
              actualHours: entry.actualhours,
              billableHours: entry.billablehours,
              availableHours: entry.availablehours,
              numberOfTechnicians: entry.numberoftechnicians,
              notes: entry.notes || ''
            });
          }

          resolve(entries);
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  };

  const validateBulkEntries = async (entries) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/service-jobs/bulk-import/validate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ entries })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Validation failed');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  };

  const executeBulkImport = async (entries) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/service-jobs/bulk-import/execute`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ entries })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Import failed');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  };

  const handleBulkFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setBulkFileName(file.name);
    setBulkImportError(null);

    try {
      const entries = await parseCsvFile(file);
      setBulkEntries(entries);

      const validation = await validateBulkEntries(entries);
      setBulkValidation(validation);
      setBulkStep('review');
    } catch (error) {
      setBulkImportError(error.message);
    }
  };

  const handleBulkExecute = async () => {
    setBulkStep('importing');
    setBulkImportError(null);
    setBulkImportSuccess(null);

    try {
      const result = await executeBulkImport(bulkValidation.normalizedEntries);
      setBulkImportSuccess(`Successfully imported ${result.imported} entries${result.skipped > 0 ? `, skipped ${result.skipped}` : ''}`);
      setBulkStep('done');
      await fetchAllData();
    } catch (error) {
      setBulkImportError(error.message);
      setBulkStep('done');
    }
  };

  const toggleAllDealerships = () => {
    if (selectedDealerships.length === dealerships.length) {
      setSelectedDealerships([]);
    } else {
      setSelectedDealerships(dealerships.map(d => d.id));
    }
  };

  const removeFilter = (type, value) => {
    if (type === 'dealership') {
      setSelectedDealerships(selectedDealerships.filter(d => d !== value));
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredEntries = allEntries.filter(entry => {
    if (selectedDealerships.length > 0 && !selectedDealerships.includes(entry.dealershipId)) {
      return false;
    }
    if (searchTerm && !entry.dealershipName.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    return true;
  });

  const sortedEntries = [...filteredEntries].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];

    if (aValue === null || aValue === undefined) return 1;
    if (bValue === null || bValue === undefined) return -1;

    if (sortField === 'date') {
      aValue = new Date(aValue);
      bValue = new Date(bValue);
    }

    if (sortField === 'netSales' || sortField === 'partsOnROs' || sortField === 'grossProfit' || sortField === 'repairOrders' || sortField === 'hours') {
      aValue = parseFloat(aValue) || 0;
      bValue = parseFloat(bValue) || 0;
    }

    if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const activeFilters = [];
  selectedDealerships.forEach(id => {
    const dealership = dealerships.find(d => d.id === id);
    if (dealership) {
      activeFilters.push({ type: 'dealership', value: id, label: dealership.name });
    }
  });

  if (loading) {
    return <LoadingState message="Loading service data..." fullscreen />;
  }

  // Inline form like DailyLogger (appears above table)

  return (
    <div className="sales-activity">
      <div className="sales-activity-header">
        <div>
          <h1>Service Input</h1>
          <p>Track daily service repair orders and hours</p>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="filter-bar">
        <div className="filter-bar-left">
          <button 
            className={`filter-button ${activeFilters.length > 0 ? 'has-filters' : ''}`}
            onClick={() => setShowFilterPanel(true)}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 4h18M3 12h12M3 20h6" strokeLinecap="round"/>
            </svg>
            Filters
            {activeFilters.length > 0 && ` (${activeFilters.length})`}
          </button>

          {activeFilters.length > 0 && (
            <div className="active-filters">
              {activeFilters.map((filter, index) => (
                <div key={index} className="active-filter-chip">
                  {filter.label}
                  <button onClick={() => removeFilter(filter.type, filter.value)}>×</button>
                </div>
              ))}
            </div>
          )}
        </div>

        {canBulkImport && (
          <div className="bulk-import-container">
            <button 
              className="bulk-import-button"
              onClick={() => {
                setShowBulkModal(true);
                setBulkStep('upload');
                setBulkValidation(null);
                setBulkImportError(null);
                setBulkImportSuccess(null);
                setBulkEntries([]);
                setBulkFileName('');
              }}
              title="Bulk Import"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z"/>
                <path d="M19.4 15a1.6 1.6 0 0 0 .33 1.8l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.6 1.6 0 0 0-1.8-.33 1.6 1.6 0 0 0-1 1.5V21a2 2 0 0 1-4 0v-.09a1.6 1.6 0 0 0-1-1.5 1.6 1.6 0 0 0-1.8.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.6 1.6 0 0 0 .33-1.8 1.6 1.6 0 0 0-1.5-1H3a2 2 0 0 1 0-4h.09a1.6 1.6 0 0 0 1.5-1 1.6 1.6 0 0 0-.33-1.8l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.6 1.6 0 0 0 1.8.33h.09a1.6 1.6 0 0 0 1-1.5V3a2 2 0 0 1 4 0v.09a1.6 1.6 0 0 0 1 1.5h.09a1.6 1.6 0 0 0 1.8-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.6 1.6 0 0 0-.33 1.8v.09a1.6 1.6 0 0 0 1.5 1H21a2 2 0 0 1 0 4h-.09a1.6 1.6 0 0 0-1.51 1Z"/>
              </svg>
            </button>
          </div>
        )}
      </div>

          {/* Filter Panel */}
      {showFilterPanel && (
        <>
          <div className="filter-overlay open" onClick={() => setShowFilterPanel(false)} />
          <div className="filter-panel open">
            <div className="filter-panel-header">
              <h3>Filters</h3>
              <button className="filter-panel-close" onClick={() => setShowFilterPanel(false)}>×</button>
            </div>

            <div className="filter-panel-content">
              <div className="filter-section">
                <div className="filter-section-header">
                  <h4>Dealerships</h4>
                  <button onClick={toggleAllDealerships} className="btn-select-all">
                    {selectedDealerships.length === 0 ? 'Select All' : 'Clear All'}
                  </button>
                </div>
                {dealerships.map(dealership => (
                  <label key={dealership.id} className="filter-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedDealerships.includes(dealership.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedDealerships([...selectedDealerships, dealership.id]);
                        } else {
                          setSelectedDealerships(selectedDealerships.filter(d => d !== dealership.id));
                        }
                      }}
                    />
                    <span>{dealership.name}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {showForm && (
        <div className="activities-form-container">
          <h2 className="form-heading">{editingEntry?.id ? 'Edit Service Entry' : 'New Service Entry'}</h2>
          <ServiceInputForm
            entry={editingEntry}
            dealerships={dealerships}
            allEntries={allEntries}
            onClose={handleCloseForm}
            onSave={() => {
              handleCloseForm();
              fetchAllData();
            }}
          />
        </div>
      )}

      <div className="activities-list">
        <div className="activities-controls">
          <div className="search-controls">
            <input
              type="text"
              placeholder="Search dealership..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          {!showForm && (
          <button className="btn-new-sale" onClick={handleNewEntry}>
            + New Entry
          </button>
          )}
        </div>

        {/* Tab Navigation */}
        <div className="view-mode-tabs" style={{ display: 'flex', gap: '8px', marginBottom: '16px', borderBottom: '2px solid rgba(255, 255, 255, 0.1)', paddingBottom: '0' }}>
          <button
            className={viewMode === 'service' ? 'tab-active' : 'tab-inactive'}
            onClick={() => setViewMode('service')}
            style={{
              padding: '12px 24px',
              background: viewMode === 'service' ? 'rgba(255, 122, 0, 0.2)' : 'transparent',
              color: viewMode === 'service' ? '#ff7a00' : 'rgba(255, 255, 255, 0.6)',
              border: 'none',
              borderBottom: viewMode === 'service' ? '2px solid #ff7a00' : '2px solid transparent',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s'
            }}





          >
            Service Data
          </button>
          <button
            className={viewMode === 'labor' ? 'tab-active' : 'tab-inactive'}
            onClick={() => setViewMode('labor')}
            style={{
              padding: '12px 24px',
              background: viewMode === 'labor' ? 'rgba(255, 122, 0, 0.2)' : 'transparent',
              color: viewMode === 'labor' ? '#ff7a00' : 'rgba(255, 255, 255, 0.6)',
              border: 'none',
              borderBottom: viewMode === 'labor' ? '2px solid #ff7a00' : '2px solid transparent',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s'
            }}
          >
            Labor Data
          </button>
        </div>

        {sortedEntries.length === 0 ? (
          <div className="no-activities">
            <p>No entries found</p>
            {allEntries.length > 0 && <p className="filter-hint">Try adjusting your filters</p>}
            <button onClick={handleNewEntry}>Add first entry</button>
          </div>
        ) : (
          <div className="activities-table-container">
            <table className="activities-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('dealershipName')} className="sortable">
                    Dealership {sortField === 'dealershipName' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('date')} className="sortable">
                    Date {sortField === 'date' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  {viewMode === 'service' ? (
                    <>
                      <th onClick={() => handleSort('repairOrders')} className="sortable">
                        Repair Orders {sortField === 'repairOrders' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th onClick={() => handleSort('hours')} className="sortable">
                        Hours {sortField === 'hours' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th onClick={() => handleSort('netSales')} className="sortable">
                        NET ($) {sortField === 'netSales' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th onClick={() => handleSort('partsOnROs')} className="sortable">
                        Parts on RO's ($) {sortField === 'partsOnROs' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th onClick={() => handleSort('grossProfit')} className="sortable">
                        Gross Profit ($) {sortField === 'grossProfit' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                    </>
                  ) : (
                    <>
                      <th onClick={() => handleSort('actualHours')} className="sortable">
                        Actual Hours {sortField === 'actualHours' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th onClick={() => handleSort('billableHours')} className="sortable">
                        Billable Hours {sortField === 'billableHours' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th onClick={() => handleSort('availableHours')} className="sortable">
                        Available Hours {sortField === 'availableHours' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th onClick={() => handleSort('numberOfTechnicians')} className="sortable">
                        # Technicians {sortField === 'numberOfTechnicians' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                    </>
                  )}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedEntries.map((entry) => (
                  <tr key={entry.id} className={entry.source === 'TALON' ? 'talon-synced-row' : ''}>
                    <td>
                      {entry.dealershipName}
                      {entry.source === 'TALON' && <span className="talon-badge">TALON</span>}
                    </td>
                    <td>{toLocalDateString(entry.date)}</td>
                    {viewMode === 'service' ? (
                      <>
                        <td>{entry.repairOrders}</td>
                        <td>{Math.round(entry.hours * 10) / 10}</td>
                        <td>${(Math.round(entry.netSales * 100) / 100).toLocaleString('en-US', {minimumFractionDigits: 0, maximumFractionDigits: 0})}</td>
                        <td>${(Math.round((entry.partsOnROs || 0) * 100) / 100).toLocaleString('en-US', {minimumFractionDigits: 0, maximumFractionDigits: 0})}</td>
                        <td>${(Math.round(entry.grossProfit * 100) / 100).toLocaleString('en-US', {minimumFractionDigits: 0, maximumFractionDigits: 0})}</td>
                      </>
                    ) : (
                      <>
                        <td>{entry.actualHours != null ? (Math.round(entry.actualHours * 10) / 10) : '-'}</td>
                        <td>{entry.billableHours != null ? (Math.round(entry.billableHours * 10) / 10) : '-'}</td>
                        <td>{entry.availableHours != null ? (Math.round(entry.availableHours * 10) / 10) : '-'}</td>
                        <td>{entry.numberOfTechnicians != null ? entry.numberOfTechnicians : '-'}</td>
                      </>
                    )}
                    <td>
                      {entry.source === 'TALON' ? (
                        <span className="talon-readonly-label">Synced</span>
                      ) : (
                        <div className="table-actions">
                          <button
                            className="btn-edit"
                            onClick={() => handleEditEntry(entry)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn-delete"
                            onClick={() => handleDeleteEntry(entry.id)}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      <ConfirmModal
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
        onConfirm={() => setAlertModal({ ...alertModal, isOpen: false })}
        title={alertModal.title}
        message={alertModal.message}
        confirmStyle="primary"
        hideCancel={true}
        confirmText="OK"
      />
      </div>
    </div>
  );
}

function ServiceInputForm({ entry, dealerships, onClose, onSave, allEntries }) {
  const [formData, setFormData] = useState({
    date: entry?.date ? toLocalDateString(entry.date) : getLocalDateString(),
    dealershipId: entry?.dealershipId || '',
    repairOrders: entry?.repairOrders || 0,
    hours: entry?.hours || 0,
    netSales: entry?.netSales || 0,
    partsOnROs: entry?.partsOnROs || 0,
    grossProfit: entry?.grossProfit || 0,
    actualHours: entry?.actualHours || 0,
    billableHours: entry?.billableHours || 0,
    availableHours: entry?.availableHours || 0,
    numberOfTechnicians: entry?.numberOfTechnicians || 0,
    notes: entry?.notes || ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [duplicateModal, setDuplicateModal] = useState({
    show: false,
    entry: null,
    resolve: null
  });
  const token = localStorage.getItem('token');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDuplicateAction = (action) => {
    if (duplicateModal.resolve) {
      duplicateModal.resolve(action);
    }
    setDuplicateModal({ show: false, entry: null, resolve: null });
  };

  const handleReplaceEntry = async (existingEntry) => {
    try {
      setLoading(true);
      // Delete the existing entry
      const deleteResponse = await fetch(
        `${API_URL}/api/service-jobs/${existingEntry.id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!deleteResponse.ok) {
        throw new Error('Failed to delete existing entry');
      }

      // Now save the new entry using the same logic from handleSubmit
      const hasServiceData = (
        parseInt(formData.repairOrders) > 0 ||
        parseFloat(formData.hours) > 0 ||
        parseFloat(formData.netSales) > 0 ||
        parseFloat(formData.partsOnROs) > 0 ||
        parseFloat(formData.grossProfit) > 0
      );

      const hasLaborData = (
        parseFloat(formData.actualHours) > 0 ||
        parseFloat(formData.billableHours) > 0 ||
        parseFloat(formData.availableHours) > 0 ||
        parseInt(formData.numberOfTechnicians) > 0
      );

      const payload = {
        date: formData.date,
        dealershipId: formData.dealershipId,
        notes: formData.notes || ''
      };

      if (hasServiceData) {
        payload.repairOrders = parseInt(formData.repairOrders) || 0;
        payload.hours = parseFloat(formData.hours) || 0;
        payload.netSales = parseFloat(formData.netSales) || 0;
        payload.partsOnROs = parseFloat(formData.partsOnROs) || 0;
        payload.grossProfit = parseFloat(formData.grossProfit) || 0;
      }

      if (hasLaborData) {
        payload.actualHours = parseFloat(formData.actualHours) || 0;
        payload.billableHours = parseFloat(formData.billableHours) || 0;
        payload.availableHours = parseFloat(formData.availableHours) || 0;
        payload.numberOfTechnicians = parseInt(formData.numberOfTechnicians) || 0;
      }

      const response = await fetch(
        `${API_URL}/api/dealerships/${formData.dealershipId}/service-jobs`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        }
      );

      if (response.ok) {
        setMessage({ type: 'success', text: 'Entry replaced successfully!' });
        setTimeout(onSave, 500);
      } else {
        const error = await response.json();
        throw new Error(error.message || 'Failed to save entry');
      }
    } catch (error) {
      console.error('Error replacing entry:', error);
      setMessage({ type: 'error', text: 'Failed to replace entry. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleCombineEntry = async (existingEntry) => {
    try {
      // Combine the values
      const combinedData = {
        date: formData.date,
        dealershipId: formData.dealershipId,
        repairOrders: (parseInt(formData.repairOrders) || 0) + (existingEntry.repairOrders || 0),
        hours: (parseFloat(formData.hours) || 0) + (existingEntry.hours || 0),
        netSales: (parseFloat(formData.netSales) || 0) + (existingEntry.netSales || 0),
        partsOnROs: (parseFloat(formData.partsOnROs) || 0) + (existingEntry.partsOnROs || 0),
        grossProfit: (parseFloat(formData.grossProfit) || 0) + (existingEntry.grossProfit || 0),
        actualHours: (parseFloat(formData.actualHours) || 0) + (existingEntry.actualHours || 0),
        billableHours: (parseFloat(formData.billableHours) || 0) + (existingEntry.billableHours || 0),
        availableHours: (parseFloat(formData.availableHours) || 0) + (existingEntry.availableHours || 0),
        numberOfTechnicians: (parseInt(formData.numberOfTechnicians) || 0) + (existingEntry.numberOfTechnicians || 0),
        notes: existingEntry.notes ? `${existingEntry.notes}\n${formData.notes || ''}` : (formData.notes || '')
      };

      // Update the existing entry with combined data
      const response = await fetch(
        `${API_URL}/api/service-jobs/${existingEntry.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(combinedData)
        }
      );

      if (response.ok) {
        setMessage({ type: 'success', text: 'Entries combined successfully!' });
        setTimeout(onSave, 500);
      } else {
        const error = await response.json();
        throw new Error(error.message || 'Failed to combine entries');
      }
    } catch (error) {
      console.error('Error combining entries:', error);
      setMessage({ type: 'error', text: 'Failed to combine entries. Please try again.' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      // Check if service data has any non-zero values
      const hasServiceData = (
        parseInt(formData.repairOrders) > 0 ||
        parseFloat(formData.hours) > 0 ||
        parseFloat(formData.netSales) > 0 ||
        parseFloat(formData.partsOnROs) > 0 ||
        parseFloat(formData.grossProfit) > 0
      );

      // Check if labor data has any non-zero values
      const hasLaborData = (
        parseFloat(formData.actualHours) > 0 ||
        parseFloat(formData.billableHours) > 0 ||
        parseFloat(formData.availableHours) > 0 ||
        parseInt(formData.numberOfTechnicians) > 0
      );

      // Validate that at least one type of data is provided
      if (!hasServiceData && !hasLaborData) {
        setMessage({ type: 'error', text: 'Please enter either service data or labor data (or both)' });
        setLoading(false);
        return;
      }

      const isEditing = entry?.id;

      // Check for duplicate entry (only for new entries, not edits)
      if (!isEditing) {
        const dateString = formData.date;
        const existingEntry = allEntries.find(
          e => e.dealershipId === formData.dealershipId &&
          e.date && toLocalDateString(e.date) === dateString &&
          e.id !== entry?.id
        );

        if (existingEntry) {
          setLoading(false);
          // Show duplicate modal and wait for user action
          const action = await new Promise((resolve) => {
            setDuplicateModal({
              show: true,
              entry: existingEntry,
              resolve
            });
          });

          if (action === 'cancel') {
            return;
          } else if (action === 'replace') {
            await handleReplaceEntry(existingEntry);
            return;
          } else if (action === 'combine') {
            await handleCombineEntry(existingEntry);
            return;
          }
        }
      }

      const url = isEditing
        ? `${API_URL}/api/service-jobs/${entry.id}`
        : `${API_URL}/api/dealerships/${formData.dealershipId}/service-jobs`;

      // Build payload - only include non-zero values
      const payload = {
        date: formData.date,
        dealershipId: formData.dealershipId,
        notes: formData.notes || ''
      };

      // Add service data only if there's actual service data
      if (hasServiceData) {
        payload.repairOrders = parseInt(formData.repairOrders) || 0;
        payload.hours = parseFloat(formData.hours) || 0;
        payload.netSales = parseFloat(formData.netSales) || 0;
        payload.partsOnROs = parseFloat(formData.partsOnROs) || 0;
        payload.grossProfit = parseFloat(formData.grossProfit) || 0;
      }

      // Add labor data only if there's actual labor data
      if (hasLaborData) {
        payload.actualHours = parseFloat(formData.actualHours) || 0;
        payload.billableHours = parseFloat(formData.billableHours) || 0;
        payload.availableHours = parseFloat(formData.availableHours) || 0;
        payload.numberOfTechnicians = parseInt(formData.numberOfTechnicians) || 0;
      }

      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Entry saved successfully!' });
        setTimeout(onSave, 500);
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.message || 'Failed to save entry' });
      }
    } catch (error) {
      console.error('Error:', error);
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="form-container">
      <form onSubmit={handleSubmit} className="entry-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="dealership">Dealership</label>
              <select
                id="dealership"
                name="dealershipId"
                value={formData.dealershipId || ''}
                onChange={handleChange}
                className="form-input"
                required
              >
                <option value="">Select a dealership...</option>
                {dealerships.map(d => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="date">Date</label>
              <DatePicker
                asString
                hideLabel
                className="dp-form"
                selectedDate={formData.date || ''}
                onDateChange={(val) => handleChange({ target: { name: 'date', value: val } })}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="repairOrders">Repair Orders (NUMBER)</label>
              <input
                type="text"
                id="repairOrders"
                name="repairOrders"
                value={formData.repairOrders}
                onChange={handleChange}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label htmlFor="hours">Hours (NUMBER)</label>
              <input
                type="text"
                id="hours"
                name="hours"
                value={formData.hours}
                onChange={handleChange}
                className="form-input"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="netSales">Net Sales ($)</label>
              <input
                type="text"
                id="netSales"
                name="netSales"
                value={formData.netSales}
                onChange={handleChange}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label htmlFor="grossProfit">Gross Profit ($)</label>
              <input
                type="text"
                id="grossProfit"
                name="grossProfit"
                value={formData.grossProfit}
                onChange={handleChange}
                className="form-input"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="partsOnROs">Parts Sold on Repair Orders ($)</label>
              <input
                type="text"
                id="partsOnROs"
                name="partsOnROs"
                value={formData.partsOnROs}
                onChange={handleChange}
                className="form-input"
              />
            </div>
          </div>

          <div style={{ marginTop: '24px', marginBottom: '16px', paddingTop: '16px', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: 'rgba(255, 255, 255, 0.9)' }}>Daily Technicians’ Performance</h3>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="actualHours">Actual Hours (NUMBER)</label>
              <input
                type="text"
                id="actualHours"
                name="actualHours"
                value={formData.actualHours}
                onChange={handleChange}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label htmlFor="billableHours">Billable Hours (NUMBER)</label>
              <input
                type="text"
                id="billableHours"
                name="billableHours"
                value={formData.billableHours}
                onChange={handleChange}
                className="form-input"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="availableHours">Available Hours (NUMBER)</label>
              <input
                type="text"
                id="availableHours"
                name="availableHours"
                value={formData.availableHours}
                onChange={handleChange}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label htmlFor="numberOfTechnicians">Number of Technicians</label>
              <input
                type="text"
                id="numberOfTechnicians"
                name="numberOfTechnicians"
                value={formData.numberOfTechnicians}
                onChange={handleChange}
                className="form-input"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group full-width">
              <label htmlFor="notes">Notes</label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes || ''}
                onChange={handleChange}
                className="form-input"
                rows="4"
              />
            </div>
          </div>

          {message.text && (
            <div className={`form-message ${message.type}`}>
              {message.text}
            </div>
          )}

          <div className="form-actions">
            <button type="button" className="cancel-btn" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? 'Saving...' : 'Save Entry'}
            </button>
          </div>
      </form>

      {/* Duplicate Entry Modal */}
      {duplicateModal.show && duplicateModal.entry && (
        <>
          <div className="modal-overlay" onClick={() => handleDuplicateAction('cancel')} />
          <div className="duplicate-modal">
            <div className="modal-content">
              <h2>Entry Already Exists</h2>
              <p className="modal-message">
                An entry already exists for <strong>{duplicateModal.entry.dealershipName}</strong> on <strong>{toLocalDateString(duplicateModal.entry.date)}</strong>.
              </p>
              <p className="modal-subtext">
                What would you like to do?
              </p>
              <div className="modal-actions">
                <button
                  className="modal-btn modal-btn-replace"
                  onClick={() => handleDuplicateAction('replace')}
                >
                  Replace Entry
                </button>
                <button
                  className="modal-btn modal-btn-combine"
                  onClick={() => handleDuplicateAction('combine')}
                >
                  Combine Entries
                </button>
                <button
                  className="modal-btn modal-btn-cancel"
                  onClick={() => handleDuplicateAction('cancel')}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default ServiceInput;
